import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  SequenceMenu,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { usePermissions } from 'lib/rbac'
import { useState } from 'react'

const VoidInvoiceModal = (props: {
  invoiceId: string
  onSuccess?: () => void
  onClose: () => void
}) => {
  const { displayNotification } = useNotifications()

  const invoiceQuery = useQuery(dashboard20240730Client.getInvoice, {
    id: props.invoiceId
  })
  const customerQuery = useQuery(dashboard20240730Client.getCustomer, {
    id: invoiceQuery.data?.customerId ?? '',
    enabled: Boolean(invoiceQuery.data?.customerId)
  })

  const voidMutation = useMutation(dashboard20240730Client.postVoidInvoice, {
    onSuccess: () => {
      displayNotification('Invoice marked as void', {
        type: 'success'
      })
    },
    onError: () => {
      displayNotification('Failed to void invoice', {
        type: 'error'
      })
    }
  })

  const isLoading = invoiceQuery.isPending || customerQuery.isPending

  return (
    <SimpleModal
      data-testid="invoices.voidInvoice.modal"
      variant="dialog"
      title="Void invoice"
      submitButtonText="Void invoice"
      submittingButtonText="Voiding..."
      isLoading={isLoading}
      isOpen
      onClose={props.onClose}
      onSubmit={async () => {
        await voidMutation.mutateAsync({ id: props.invoiceId })
        props.onSuccess?.()
        return true
      }}
    >
      {`Mark this invoice for ${customerQuery.data?.legalName} as void. This action cannot be undone.`}
    </SimpleModal>
  )
}

export const VoidInvoice = (props: {
  invoiceId: string
  onSuccess?: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { can } = usePermissions()

  if (!can('org.invoice:update')) {
    return null
  }

  return (
    <>
      <SequenceMenu.Item
        destructive
        data-testid="invoices.voidInvoice.menuItem"
        uuid="void-invoice"
        label="Void invoice"
        onClick={e => {
          e.stopPropagation()
          setIsOpen(true)
        }}
      />
      {isOpen && (
        <VoidInvoiceModal
          invoiceId={props.invoiceId}
          onSuccess={props.onSuccess}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  )
}
